import { useCallback, useEffect, useState } from "react";
import { Discipline, Startgroup } from "../api/Dtos";
import { useGlobalContext } from "../Layout/GlobalState";
import { DELETE, GET, POST } from "../api/BackendApi";
import { timeInMsToString, dateTimeInMsToString } from "./ResultService";
import { start } from "repl";
import { DisciplineStartgroupMap } from "../DisciplineStartgroupMapComponent";

// type DisplayStartgroups = Startgroup & {
//   starttime_planned_str: string;
//   closetime_planned_str: string;
//   starttime_actual_str?: string;
//   closetime_actual_str?: string;
// };

export function useStartgroupsFromBackend() {
  const globalContext = useGlobalContext();
  const [startgroups, setStartgroups] = useState<Array<Startgroup>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Get startgroups from backend for the selected competition and filter by competition timeframe
  useEffect(() => {
    if (globalContext.competition) {
      setLoading(true);
      GET.allStartgroupsInCompetition(globalContext.competition.id)
        .then((startgroups: Array<Startgroup>) => {
          const competitionStart = globalContext.competition?.startTime;
          const competitionEnd = globalContext.competition?.endTime;

          const filtered = startgroups
            .filter((sg) => {
              if (competitionStart != null && sg.starttime_planned < competitionStart) {
                return false;
              }
              if (competitionEnd != null && sg.closetime_planned > competitionEnd) {
                return false;
              }
              return true;
            })
            .sort((a, b) => a.starttime_planned - b.starttime_planned);

          setStartgroups(filtered);
        })
        .catch((error) => {
          console.error("Error fetching startgroups:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [globalContext.competition]);

  return { startgroups, setStartgroups, loading };
}

export function startgroupService() {
  const [startgroups, setStartgroups] = useState<Array<Startgroup>>([]);
  const globalContext = useGlobalContext();

  const fetchStartgroups = async (): Promise<Startgroup[]> => {
    try {
      if (globalContext.competition) {
        const startgroups = await GET.allStartgroupsInCompetition(globalContext.competition.id);
        return startgroups;
      } else {
        return startgroups;
      }
    }
    catch (error) {
      console.error('Error fetch all startgroupsByCompetitionTag: ', error);
      throw error;
    }
  };

  const GetStartgroups = async () => {
    try {
      const startgroups: Startgroup[] = await fetchStartgroups();
      return startgroups;
    } catch (error) {
      console.error('Error fetch all startgroupsBtCompetitionTag: ', error);
      throw error;
    }
  };

  return { startgroups: GetStartgroups };
};


export interface DisciplineStartgroupMapping {
  id: number;
  startgroup: Startgroup;
  discipline: Discipline;
}

export function useDisciplineStartgroupMappings() {
  const globalContext = useGlobalContext();
  const [persistedMappings, setPersistedMappings] = useState<Array<DisciplineStartgroupMapping>>([]);

  useEffect(() => {
    if (globalContext.competition) {
      GET.allDisciplineStartgroupMaps()
      .then((response) => {
        const competitionStart = globalContext.competition?.startTime;
        const competitionEnd = globalContext.competition?.endTime;

        const filteredStartgroups = globalContext.competition?.startgroups.filter((sg) => {
          if (competitionStart != null && sg.starttime_planned < competitionStart) {
            return false;
          }
          if (competitionEnd != null && sg.closetime_planned > competitionEnd) {
            return false;
          }
          return true;
        });

        const startgroupIds = filteredStartgroups?.map(s => s.id);
        const mappingsForCompetition = response.filter(m => startgroupIds?.includes(m.startgroupId));

        return mappingsForCompetition.map(m => ({
          id: m.id,
          discipline: globalContext.competition?.disciplines.find((d) => d.id === m.disciplineId),
          startgroup: filteredStartgroups?.find((s) => s.id === m.startgroupId)
        } as DisciplineStartgroupMapping));
      })
      .then((parsedResponse) => {
        setPersistedMappings(parsedResponse.sort((a,b) => a.startgroup.starttime_planned - b.startgroup.starttime_planned));
      })
    }
  }, []);

  const saveMapping = useCallback(async (startgroup: Startgroup, disciplines: Array<Discipline>) => {
    const existingMappings = persistedMappings.filter(m => m.startgroup.id === startgroup.id);
    const newTargetMappings = disciplines.map(d => ({id:-1, discipline: d, startgroup: startgroup} as DisciplineStartgroupMapping));

    const mappingsToDelete = existingMappings.filter(m => !(disciplines.map(d => d.id).includes(m.discipline.id)));
    const mappingsToCreate = newTargetMappings.filter(m => !(existingMappings.map(m => m.discipline.id).includes(m.discipline.id)));

    const added = [] as Array<DisciplineStartgroupMapping>;
    for (let m of mappingsToCreate) {
      try {
        const newMapping = await POST.disciplineStartgroupMapping(m.startgroup.id, m.discipline.id);

        if (m.discipline.id === newMapping.disciplineId && m.startgroup.id === newMapping.startgroupId) {
          added.push({...m, id: newMapping.id});
        }
      }
      catch (e) {
        console.error("Failed to add new DisciplineStartgroupMapping", m);
      }
    }

    const deleted = [] as Array<DisciplineStartgroupMapping>;
    for (let m of mappingsToDelete) {
      try {
        const removedMapping = await DELETE.disciplineStartgroupMapping(m.id);

        if (m.discipline.id === removedMapping.disciplineId && m.startgroup.id === removedMapping.startgroupId) {
          deleted.push({...m, id: removedMapping.id});
        }
      }
      catch (e) {
        console.error("Failed to remove DisciplineStartgroupMapping", m);
      }
    }

    setPersistedMappings((old) => {
      const updatedMappings = [...old, ...added].sort((a, b) => a.startgroup.starttime_planned - b.startgroup.starttime_planned);
      return updatedMappings.filter(m => !deleted.map(d => d.id).includes(m.id));
    })
  }, [persistedMappings, setPersistedMappings]);

  return {persistedMappings, saveMapping};
}